import { useMemo } from "react";
import { ReactECharts } from "@/common/components/react-echarts";
import { useFormatNumber } from "@/common/hooks/use-format-number";
import { fractionDigitsOf } from "@/common/lib/format/format-number";
import type {
  EChartsOption,
  BarSeriesOption,
  TooltipComponentFormatterCallback,
  TooltipComponentFormatterCallbackParams,
  TooltipComponentOption,
} from "echarts";
import { sortUsdFirst } from "@/common/lib/utils/sort";
import { getChartColors } from "@/common/lib/chart/color";
import {
  type BaseChartProps,
  createCommonAxisConfig,
  createCommonGridConfig,
  createEmptyChartOption,
} from "./utils";
import { formatDateAxis } from "@/common/lib/chart/chart";
import { useLegendSelection } from "../../lib/legend-selection";
import {
  ChartPeriodTable,
  type ChartPeriodSeries,
} from "../../components/chart-period-table";
/**
 * Single Date Balance Chart component
 * Displays data as a bar chart with multiple commodities shown separately
 */
export function SingleDateBalanceChart({
  data,
  interval,
  primarySeries = "USD",
  inverted,
  labelledBy,
  legendSelection,
  onLegendSelectionChange,
}: BaseChartProps) {
  const formatNum = useFormatNumber();
  const built = useMemo((): {
    chartOption: EChartsOption;
    periodSeries: ChartPeriodSeries[];
    units: string[];
  } => {
    if (!data || data.length === 0) {
      return {
        chartOption: createEmptyChartOption(),
        periodSeries: [],
        units: [],
      };
    }

    // Extract all unique commodities from the data
    const commodities = new Set<string>();
    data.forEach((item) => {
      Object.keys(item.balance).forEach((commodity) => {
        commodities.add(commodity);
      });
    });

    const commodityList = sortUsdFirst(Array.from(commodities), primarySeries);

    // Prepare data for each commodity
    const seriesData = commodityList.map((commodity) => {
      const values = data.map((item) => {
        const balance = item.balance[commodity];
        if (balance === null || balance === undefined) {
          return 0;
        }
        // Convert to number, handling both string and number types
        const numericValue =
          typeof balance === "string" ? parseFloat(balance) : Number(balance);
        return isNaN(numericValue)
          ? 0
          : inverted
            ? -numericValue
            : numericValue;
      });

      return {
        name: commodity,
        type: "bar",
        data: values,
        emphasis: {
          focus: "series",
        },
      } satisfies BarSeriesOption;
    });

    // Prepare x-axis data (dates)
    const dates = data.map((item) => item.date);

    // The bars carry numbers, so remember how much precision each source
    // decimal had before it was parsed.
    const sourceDigits = new Map<string, number>();
    data.forEach((item) => {
      Object.entries(item.balance).forEach(([commodity, raw]) => {
        sourceDigits.set(
          `${item.date}\u0000${commodity}`,
          fractionDigitsOf(raw as string),
        );
      });
    });

    const tooltip: TooltipComponentOption = {
      trigger: "axis",
      axisPointer: {
        type: "shadow",
      },
      formatter: ((params: TooltipComponentFormatterCallbackParams) => {
        if (Array.isArray(params)) {
          let result = `<div><strong>${formatDateAxis(params[0].name, interval ?? "monthly")}</strong></div>`;
          params.forEach((param) => {
            const value = param.value;
            const digits =
              sourceDigits.get(`${param.name}\u0000${param.seriesName}`) ?? 0;

            const formattedValue =
              typeof value === "number"
                ? value >= 0
                  ? `+${formatNum(value, param.seriesName, digits)}`
                  : formatNum(value, param.seriesName, digits)
                : "0";
            result += `<div style="color: ${param.color}">
              <span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:${param.color};"></span>
              ${param.seriesName}: <span style="color: ${param.color}; font-weight: bold;">${formattedValue}</span>
            </div>`;
          });
          return result;
        }
        return "";
      }) as TooltipComponentFormatterCallback<TooltipComponentFormatterCallbackParams>,
    };

    const { xAxis, yAxis } = createCommonAxisConfig(dates, interval);

    return {
      units: commodityList,
      periodSeries: seriesData.map((item) => ({
        unit: item.name,
        values: item.data,
      })),
      chartOption: {
        tooltip,
        legend: {
          data: commodityList,
          bottom: 0,
          type: "scroll",
          pageButtonItemGap: 5,
        },
        grid: createCommonGridConfig(),
        xAxis,
        yAxis,
        series: seriesData,
        color: getChartColors(),
        animation: true,
        animationDuration: 1000,
        animationEasing: "cubicOut",
      },
    };
  }, [data, interval, primarySeries, inverted, formatNum]);
  // The reader's units, else the primary unit, else the first unit the chart
  // has: a scope without the primary unit must not start all hidden (w4/997).
  const { chartOption, onLegendSelectChanged } = useLegendSelection(
    built.chartOption,
    built.units,
    primarySeries,
    { legendSelection, onLegendSelectionChange },
  );

  return (
    <div className="w-full">
      <ReactECharts
        option={chartOption}
        style={{ height: "250px", width: "100%" }}
        className="w-full"
        onLegendSelectChanged={onLegendSelectChanged}
      />
      {labelledBy && (
        <ChartPeriodTable
          data={data}
          series={built.periodSeries}
          labelledBy={labelledBy}
        />
      )}
    </div>
  );
}
